/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState, type RefObject } from 'react';
import { ModalAwareScrollView } from './ModalAwareScrollView';
import {
  Platform,
  RefreshControl,
  StyleSheet,
  View,
  type ScrollViewProps,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { ticketColors, webOperationalScrollBindings } from '../theme/public-api';

const SCROLL_BOTTOM_THRESHOLD = 12;
const DEFAULT_MAX_HEIGHT = 420;
const DEFAULT_MAX_HEIGHT_WEB = 720;

type Props = {
  children: React.ReactNode;
  maxHeight?: number;
  contentContainerStyle?: StyleProp<ViewStyle>;
  keyboardShouldPersistTaps?: ScrollViewProps['keyboardShouldPersistTaps'];
  /** Padding extra bajo el contenido cuando el teclado está abierto. */
  keyboardBottomPadding?: number;
  /** View del contenido scrolleable. */
  contentRef?: RefObject<View | null>;
  refreshing?: boolean;
  onRefresh?: () => void;
};

export const ModalFormScroll = React.forwardRef<KeyboardAwareScrollViewRef, Props>(function ModalFormScroll(
  {
    children,
    maxHeight,
    contentContainerStyle,
    keyboardShouldPersistTaps = 'handled',
    keyboardBottomPadding,
    contentRef,
    refreshing = false,
    onRefresh,
  },
  ref,
) {
  const effectiveMaxHeight = maxHeight ?? (Platform.OS === 'web' ? DEFAULT_MAX_HEIGHT_WEB : DEFAULT_MAX_HEIGHT);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);

  const scrollEnabled = viewportHeight > 0 && contentHeight > viewportHeight + SCROLL_BOTTOM_THRESHOLD;

  return (
    <View style={[styles.root, { maxHeight: effectiveMaxHeight }]}>
      <ModalAwareScrollView
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any}
        {...webOperationalScrollBindings(styles.scroll)}
        contentContainerStyle={contentContainerStyle}
        keyboardShouldPersistTaps={keyboardShouldPersistTaps}
        bottomOffset={keyboardBottomPadding}
        showsVerticalScrollIndicator={Platform.OS === 'web' && scrollEnabled}
        scrollEnabled={scrollEnabled || onRefresh != null}
        bounces={scrollEnabled || onRefresh != null}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[ticketColors.ink2]}
              tintColor={ticketColors.ink2}
            />
          ) : undefined
        }
        onLayout={(event) => setViewportHeight(event.nativeEvent.layout.height)}
        onContentSizeChange={(_, height) => setContentHeight(height)}
      >
        <View ref={contentRef} collapsable={false}>
          {children}
        </View>
      </ModalAwareScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  root: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 14,
    // Permite ceder altura cuando la card del modal está acotada al viewport:
    // sin esto el scroll mantiene su maxHeight y empuja el footer fuera de pantalla.
    flexShrink: 1,
  },
  scroll: {
    flexGrow: 0,
  },
});
